import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';

import { MobileBottomSheet } from '@/components/ui/MobileBottomSheet';
import { MobileButton } from '@/components/ui/MobileButton';
import { MobileEmptyState } from '@/components/ui/MobileEmptyState';
import { MobileSearchInput } from '@/components/ui/MobileSearchInput';
import { MobileSkeleton } from '@/components/ui/MobileSkeleton';
import { loadPricedItems, type PricingTarget } from '@/app/services/catalogService';
import type { Category, PricedItem, TaxRate, Unit } from '@/app/types/item';

import { CategoryChips } from './CategoryChips';
import { ProductRow } from './ProductRow';

interface AddProductsSheetProps {
    open: boolean;
    onClose: () => void;
    /** Who the products are priced for: the order being edited. */
    pricingTarget: PricingTarget;
    units: Unit[] | undefined;
    taxRates: TaxRate[] | undefined;
    categories: Category[] | undefined;
    /** Quantity already on the order for an item, so rows open pre-filled. */
    getQuantity: (itemId: number) => number;
    onQuantityChange: (item: PricedItem, quantity: number) => void;
    onShowTiers: (item: PricedItem) => void;
    /** How many lines the order has, shown on the done button. */
    lineCount: number;
}

/**
 * Product browser for adding lines to an existing order.
 *
 * Deliberately the same search / category / ProductRow arrangement as the new-order
 * screen, and it writes straight into the editor — a product tapped here is on the
 * order immediately, with no separate "add" step to forget.
 */
export function AddProductsSheet({
    open,
    onClose,
    pricingTarget,
    units,
    taxRates,
    categories,
    getQuantity,
    onQuantityChange,
    onShowTiers,
    lineCount,
}: AddProductsSheetProps) {
    const [rawQuery, setRawQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [categoryId, setCategoryId] = useState<number | null>(null);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedQuery(rawQuery.trim()), 300);
        return () => clearTimeout(t);
    }, [rawQuery]);

    // Land on the first category rather than an empty screen: there is no
    // "usually ordered" list to fall back on once an order already exists.
    useEffect(() => {
        if (open && categoryId === null && categories?.length) {
            setCategoryId(categories[0].id);
        }
    }, [open, categoryId, categories]);

    const searching = debouncedQuery.length >= 2;
    const referenceReady = !!units && !!taxRates;

    const { data, isLoading, isFetching } = useQuery({
        queryKey: ['so-edit-products', debouncedQuery, categoryId, pricingTarget],
        enabled: open && referenceReady && (searching || categoryId !== null),
        staleTime: 30_000,
        queryFn: () =>
            loadPricedItems(
                searching ? { searchQuery: debouncedQuery } : { categoryId: categoryId! },
                units!,
                taxRates!,
                pricingTarget
            ),
    });

    const items = data?.items ?? [];
    const listLoading = isLoading || (isFetching && !data);

    return (
        <MobileBottomSheet
            open={open}
            onClose={onClose}
            title="Add products"
            heightMode="full"
        >
            <div className="flex flex-col h-full">
                <div className="flex-shrink-0 border-b border-border">
                    <div className="px-4 pt-3">
                        <MobileSearchInput
                            value={rawQuery}
                            onValueChange={setRawQuery}
                            placeholder="Search products..."
                        />
                    </div>
                    {!searching && (
                        <CategoryChips
                            categories={categories ?? []}
                            selectedCategoryId={categoryId}
                            onSelect={setCategoryId}
                            showUsualChip={false}
                        />
                    )}
                </div>

                <div className="flex-1 overflow-y-auto min-h-0">
                    {listLoading ? (
                        <ProductListSkeleton />
                    ) : items.length === 0 ? (
                        <MobileEmptyState
                            icon={<Search className="h-12 w-12" />}
                            title={searching ? 'No matches' : 'No products'}
                            description={
                                searching
                                    ? 'Try a different product name or code'
                                    : 'Nothing in this category yet'
                            }
                        />
                    ) : (
                        <ul className="divide-y divide-border">
                            {items.map((item) => (
                                <ProductRow
                                    key={item.id}
                                    item={item}
                                    quantity={getQuantity(item.id)}
                                    onQuantityChange={onQuantityChange}
                                    onShowTiers={onShowTiers}
                                />
                            ))}
                        </ul>
                    )}
                </div>

                <div
                    className="border-t border-border bg-background flex-shrink-0"
                    style={{ padding: '12px 16px 16px' }}
                >
                    <MobileButton type="button" size="lg" className="w-full" onClick={onClose}>
                        Done · {lineCount} {lineCount === 1 ? 'line' : 'lines'}
                    </MobileButton>
                </div>
            </div>
        </MobileBottomSheet>
    );
}

function ProductListSkeleton() {
    return (
        <ul className="divide-y divide-border">
            {[1, 2, 3, 4, 5, 6].map((i) => (
                <li key={i} className="flex items-center gap-3 px-4 py-3 min-h-[72px]">
                    <div className="flex-1 space-y-2">
                        <MobileSkeleton className="h-4 w-48" />
                        <MobileSkeleton className="h-3 w-24" />
                    </div>
                    <MobileSkeleton className="w-11 h-11 rounded-xl" />
                </li>
            ))}
        </ul>
    );
}
